<template>
  <ExperienceCollectionTemplate
    map-source-page="destination_lp"
    :all-data-initialized="allDataInitialized"
    :article-section-experiences="articleSectionExperiences"
    :basic-title="`${destinationName}`"
    :cms-content="destinationContent"
    :destination="destination"
    :destination-tags-query="destinationTagsQuery"
    :experience-count="experienceCount"
    :experience-event-start-at-dates-query="experienceEventStartAtDatesQuery"
    :experience-sections="experienceSections"
    :experiences="experiences"
    :experiences-per-page="EXPERIENCES_PER_PAGE"
    :grid-title="`All Experiences in ${destinationName}`"
    :handle-experience-click="handleExperienceClick"
    :handle-featured-click="handleFeaturedClick"
    :handle-search-click="handleSearchClick"
    :is-loading-experiences="isLoadingExperiences"
    :is-sp="isSP"
    :itinerary-list="itineraryList"
    :on-click-contact="onClickContact"
    :on-click-partner="onClickPartner"
    :on-instant-booking-click="handleInstantBookingClick"
    :on-show-more-click="handleShowMoreClick"
    :on-sort-change="handleSortChange"
    :preference-tags-query="preferenceTagsQuery"
    :selected-sort-type="selectedSortType"
    :theme-tags-query="themeTagsQuery"
  />
</template>

<script setup lang="ts">
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useBuildUrl, useErrorCreator } from '@/wabunka-src/common/composables';
import { useLastModifiedHeader } from '@/wabunka-src/common/composables/general/useLastModifiedHeader/useLastModifiedHeader';
import ExperienceCollectionTemplate from '@/wabunka-src/common/features/Experience/components/ExperienceCollectionTemplate/ExperienceCollectionTemplate.vue';
import { useExperienceCollection } from '@/wabunka-src/common/features/Experience/composables/useExperienceCollection/useExperienceCollection';
import { useArticleSectionExperiences } from '@/wabunka-src/common/features/Featured/composables/useArticleSectionExperiences';
import { useExtractWebSectionIds } from '@/wabunka-src/common/features/Featured/composables/useExtractWebSectionIds';
import type { microCMSDraftKeyQuery } from '@/wabunka-src/common/models/microCMS';
import { togglePreference } from '@/wabunka-src/common/utils/togglePreference';
import { EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS } from '@/wabunka-src/constants/experienceSectionWebPagePath';
import { ICON_FONT_URL } from '@/wabunka-src/constants/iconFontUrl';
import { useDestinationContentQuery } from '@/wabunka-src/features/Destination/composables/query/useDestinationContentQuery';
import { useGetExperienceSectionsQuery } from '@/wabunka-src/features/Experience';

const route = useRoute();
const { createWabunkaError } = useErrorCreator();

// 定数定義
const EXPERIENCES_PER_PAGE = 18;

// ルートパラメータからdestinationを取得
const destination = route.params.destination as string;

// draftKeyの取得
const draftKey = route.query.draftKey as string | undefined;
const draftKeyQuery: microCMSDraftKeyQuery = { draftKey: draftKey || undefined };
const hasDraftKey = Boolean(draftKey);

// タグデータを取得して動的にバリデーション
const { tagDictByWebPath } = await useAsyncWabunkaTags();

// 有効なdestinationのwebPathを取得
const validDestinations = computed(() => {
  return Object.keys(tagDictByWebPath.value).filter((path) => tagDictByWebPath.value[path].tagGroupType === 'destination');
});

// 現在のURLが有効なdestinationかチェック（draftKey存在時はスキップ）
const isValidDestination = computed(() => validDestinations.value.includes(destination));

if (!hasDraftKey && !isValidDestination.value) {
  throw createWabunkaError({
    statusCode: 404,
    statusMessage: `Invalid destination: ${destination}`,
    fatal: true,
  });
}

const [destinationResult, experienceCollectionResult] = await Promise.all([
  useDestinationContentQuery({
    destination,
    draftKey: draftKeyQuery,
    server: true,
  }),
  useExperienceCollection({
    destination,
    experiencesPerPage: EXPERIENCES_PER_PAGE,
  }),
]);

const { destinationContent } = destinationResult;

useLastModifiedHeader(destinationContent.value.revisedAt);

// CMSコンテンツ（ArticleSection）で使用する体験をフィルターなしで取得
// contentsBottom にも ArticleSection が含まれるため、両方を結合して渡す
const { experiences: articleSectionExperiences } = await useArticleSectionExperiences(
  computed(() => [...(destinationContent.value.contents ?? []), ...(destinationContent.value.contentsBottom ?? [])]),
);

// CMS の experienceSectionIDs セクションで表示する体験セクションを取得
const { webSectionIds } = useExtractWebSectionIds(destinationContent);
const { experienceSections } = await useGetExperienceSectionsQuery(EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS.AREA_DETAIL, webSectionIds.value);

const {
  isSP,
  itineraryList,
  experiences,
  meta,
  isLoadingExperiences,
  experienceCount,
  hasNoResponse,
  allDataInitialized,
  destinationTagsQuery,
  themeTagsQuery,
  experienceEventStartAtDatesQuery,
  handleExperienceClick,
  onClickContact,
  onClickPartner,
  handleFeaturedClick,
  handleSearchClick,
  selectedSortType,
  handleSortChange,
  withCurrentSortQuery,
  buildUrlWithEndSlash,
} = experienceCollectionResult;

const destinationName = computed(() => {
  const tag = tagDictByWebPath.value[destination];
  return tag?.name || destinationContent.value.titleEn || destination;
});

// 体験が0件の場合はnoindexにする
// hasNoResponse ガード: 検索データ未取得（エッジキャッシュ通貨不一致等によるハイドレーション時の
// 一時的 null）を「0件」と誤認して noindex を出さない（JCAT-5203）
const shouldIndex = computed(() => hasNoResponse.value || experienceCount.value > 0);

// Show Moreボタンクリック時のハンドラ - 検索ページへ遷移（page=2）
const handleShowMoreClick = () => {
  navigateTo({ path: `/s/experiences/${destination}/`, query: withCurrentSortQuery({ page: '2' }) });
};

const INSTANT_BOOKING_WEB_PATH = 'instant-booking';

const preferenceTagsQuery = computed<string>(() => (route.query?.preferences as string) ?? '');

const handleInstantBookingClick = () => {
  const newPreferences = togglePreference(preferenceTagsQuery.value, INSTANT_BOOKING_WEB_PATH);
  const query: Record<string, string> = { page: '1' };
  if (newPreferences) {
    query.preferences = newPreferences;
  }
  navigateTo({ path: `/s/experiences/${destination}/`, query: withCurrentSortQuery(query) });
};

// メタ情報の設定
const pageTitle = computed(() => meta.value?.pageTitle || ``);
const pageDescription = computed(() => meta.value?.metaDescription || ``);
const { buildUrl } = useBuildUrl();
const ogImage = computed(() => destinationContent.value.ogImage?.url || buildUrl('/en_ogp.png'));

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  ogImage: () => ogImage.value,
  ogUrl: () => buildUrlWithEndSlash(route.path),
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: () => pageTitle.value,
  twitterDescription: () => pageDescription.value,
  twitterImage: () => ogImage.value,
});

if (hasDraftKey || !shouldIndex.value) useRobotsRule('noindex,nofollow');

useHead({
  link: [
    { rel: 'canonical', href: buildUrlWithEndSlash(route.path) },
    { rel: 'stylesheet', href: ICON_FONT_URL },
  ],
});
</script>

<style scoped>
:deep(.is-filtered) {
  border-color: #826b3d !important;
  color: #826b3d !important;
  background-color: #f8f6f1 !important;
}
</style>
